import {
  Fragment,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Box,
  Card,
  CardHeader,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  Paper,
  SvgIcon,
  Table,
  TableBody,
  TableContainer,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  withStyles,
  CircularProgress,
  useMediaQuery,
} from '@material-ui/core';

import {
  DynamicForm,
  Loader,
  LoaderOverlay,
  Modal,
} from 'components';
import { TABLES_ITEMS_PER_PAGE } from 'settings';

import { DEFAULT_PROPS, PROP_TYPES } from './constants';
import Filters from './Filters';
import useStyles from './styles';
import { DateControl } from '../DynamicForm/controls';
import { useTheme } from '@material-ui/styles';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';

const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
      backgroundColor: `${theme.palette.action.focus} !important`,
    },
  },
}))(TableRow);

function DataTable(props) {
  const {
    actions = [],
    filters,
    footers,
    footerSeparator,
    loadData = props.getDataAction,
    isLoading,
    headers,
    rows,
    pagination = {},
    searchAction,
    searchActionByDate,
    mt,
    onDeleteFilterDate,
    selectable,
    size,
    stickyHeader,
    subTitle,
    title,
    total,
    offsetTop,
    maxHeightFlag,
    maxHeightValue,
    hidden,
    noDataMessage,
    headerActionComponent,
  } = props;

  const {
    disabled: removePagination = false,
    perPageOptions = TABLES_ITEMS_PER_PAGE,
  } = pagination;
  const history = useHistory();
  const containerRef = useRef();
  const classes = useStyles();
  const [limit, setLimit] = useState(perPageOptions[0]);
  const [page, setPage] = useState(1);
  const [maxHeight, setMaxHeight] = useState(250);
  const [query, setQuery] = useState();
  const [selectedItems, setSelectedItems] = useState([]);

  const resolvedSelectedItems = Array.isArray(props.selectedIds)
    ? props.selectedIds
    : selectedItems;
  const isSelectionControlled = Array.isArray(props.selectedIds);
  const [confirmState, setConfirmState] = useState({
    open: false,
    action: null,
    item: null,
    index: null,
  });
  const { t } = useTranslation('translation', {
    keyPrefix: 'components.datatable',
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const handleSelectAllItems = (event) => {
    const currentPageIds = rows.map((item) => item.id);

    const newSelectedItems = event.target.checked
      ? Array.from(new Set([...resolvedSelectedItems, ...currentPageIds]))
      : resolvedSelectedItems.filter((id) => !currentPageIds.includes(id));

    if (!isSelectionControlled) {
      setSelectedItems(newSelectedItems);
    }

    if (props.onSelectionIdsChange) {
      props.onSelectionIdsChange(newSelectedItems);
    }

    if (props.onSelectionChange) {
      const selectedRows = rows.filter((row) => newSelectedItems.includes(row.id));
      props.onSelectionChange(selectedRows);
    }
  };

  const handleSelectOneItem = (event, itemId) => {
    let newSelectedItems;
    if (!resolvedSelectedItems.includes(itemId)) {
      newSelectedItems = [...resolvedSelectedItems, itemId];
    } else {
      newSelectedItems = resolvedSelectedItems.filter((id) => id !== itemId);
    }

    if (!isSelectionControlled) {
      setSelectedItems(newSelectedItems);
    }

    if (props.onSelectionIdsChange) {
      props.onSelectionIdsChange(newSelectedItems);
    }
    
    if (props.onSelectionChange) {
      const selectedRows = rows.filter((row) => newSelectedItems.includes(row.id));
      props.onSelectionChange(selectedRows);
    }
  };

  const handlePageChange = (event, newPage) => {
    setQuery('');
    setPage(newPage + 1);

    loadData({
      page: newPage + 1,
      limit,
    });
  };

  const handleLimitChange = (event) => {
    setQuery('');
    const newLimit = event.target.value;
    setLimit(newLimit);
    setPage(1);
    loadData && loadData({
      page: 1,
      limit: newLimit,
    });
  };

  const handleQueryChange = (event) => {
    setQuery(event.target.value);
    searchAction(event.target.value);
  };

  const handleQueryChangeByDate = (event) => {
    setQuery(event.target.value);
    searchActionByDate(event.target.value);
  };

  const handleAction = (action, item, index) => {
    if (action.confirm) {
      setConfirmState({
        open: true,
        action: action,
        item: item,
        index: index,
      });
    } else {
      action.fn(item, index);
    }
  };

  const handleConfirm = () => {
    if (confirmState.action) {
      confirmState.action.fn(confirmState.item, confirmState.index);
    }
    setConfirmState({
      open: false,
      action: null,
      item: null,
      index: null,
    });
  };

  const handleClose = () => {
    setConfirmState({
      open: false,
      action: null,
      item: null,
      index: null,
    });
  };

  const selectedAllItems = rows.length > 0 && rows.every((row) => resolvedSelectedItems.includes(row.id));
  const selectedSomeItems = !selectedAllItems && rows.some((row) => resolvedSelectedItems.includes(row.id));

  useEffect(() => {
    return history.listen(() => {
      setPage(1);
    });
  }, [history]);

  useEffect(() => {
    const windowHeight = window.innerHeight;
    const { top: elementTop } = containerRef.current.getBoundingClientRect();
    const height = windowHeight - elementTop - offsetTop;

    if (maxHeightFlag) {
      setMaxHeight(maxHeightValue);
    } else {
      setMaxHeight(height);
    }
  }, []);

  const subheaderGroups = [];
  let lastSubheader = headers[0]?.subheader;
  let group = [];

  headers.forEach((col, idx) => {
    if (col.subheader !== lastSubheader) {
      if (group.length)
        subheaderGroups.push({
          subheader: lastSubheader,
          columns: group,
        });
      group = [col];
      lastSubheader = col.subheader;
    } else {
      group.push(col);
    }
    if (idx === headers.length - 1) {
      subheaderGroups.push({
        subheader: lastSubheader,
        columns: group,
      });
    }
  });

  const hasSubheaders = headers.some((col) => !!col.subheader);

  const renderTableContent = () => (
    <Table stickyHeader={stickyHeader} aria-label="sticky table">
      <TableHead>
        {hasSubheaders && (
          <TableRow>
            {subheaderGroups.map((g, i) => (
              <TableCell
                key={i}
                align="center"
                colSpan={g.columns.length}
              >
                {g.subheader || ''}
              </TableCell>
            ))}
          </TableRow>
        )}
        <TableRow>
          {selectable && (
            <TableCell padding="checkbox" size={size}>
              <Checkbox
                checked={selectedAllItems}
                indeterminate={selectedSomeItems}
                onChange={handleSelectAllItems}
              />
            </TableCell>
          )}
          {headers.map((head, index) => {
            let cellValue = head;
            let cellAlign = 'center';

            if (typeof head === 'object') {
              cellValue = head.label;

              if (head.align) {
                cellAlign = head.align;
              }
            }

            return (
              <TableCell
                align={cellAlign}
                children={cellValue}
                key={index}
                size={size}
                {...head.minWidth && {
                  style: {
                    minWidth: head.minWidth,
                  },
                }}
              />
            );
          })}
          {actions.length > 0 && actions.some((action) => action.hidden !== true) && (<TableCell
            align="center"
            children={t('actions')}
            size={size}
          />)}
        </TableRow>
      </TableHead>

      <TableBody>
        {isLoading
          ? (
            <StyledTableRow>
              <TableCell colSpan={(headers.length || 0) + (actions.some((action) => !action.hidden) ? 1 : 0)} align="center">
                <Loader message='Cargando...' />
              </TableCell>
            </StyledTableRow>
          )
          : rows && rows.length > 0
            ? (
              rows.map((item, index) => {
                const isItemSelected = resolvedSelectedItems.includes(item.id);

                return (
                  <StyledTableRow key={index} hover>
                    {selectable && (
                      <TableCell padding="checkbox" size={size}>
                        <Checkbox
                          checked={isItemSelected}
                          onChange={(event) =>
                            handleSelectOneItem(event, item.id)
                          }
                          value={isItemSelected}
                        />
                      </TableCell>
                    )}

                    {item.values.map((colValue, iCell) => {
                      let cell = {
                        children: colValue,
                      };

                      if (
                        typeof colValue === 'object' &&
                        colValue !== null &&
                        !colValue.props
                      ) {
                        if (colValue.field) {
                          cell.children = (
                            <DynamicForm
                              fields={colValue.field}
                              size="small"
                            />
                          );
                        } else {
                          cell = {
                            align: colValue.align || 'left',
                            children: Array.isArray(colValue.label) ? (
                              colValue.label.length > 0 ? (
                                colValue.label.map((item, index) => (
                                  <span
                                    key={index}
                                    className={classes.tagItem}
                                  >
                                    {item}
                                  </span>
                                ))
                              ) : (
                                <span className={classes.emptyTagItem}>
                                  Ninguna
                                </span>
                              )
                            ) : (
                              colValue.label
                            ),
                            colSpan: colValue.colSpan,
                          };
                        }
                      }

                      return (
                        <TableCell
                          align="left"
                          style={item?.data?.style}
                          key={iCell}
                          size={size}
                          {...cell}
                        />
                      );
                    })}

                    {(actions.length > 0) && actions.some((action) => action.hidden !== true) && (
                      <TableCell
                        align="center"
                        size={size}
                        style={item?.data?.style}
                      >
                        {actions.map((action, key) => {
                          let {
                            color,
                            disabled,
                            hidden,
                            icon,
                            label,
                            title,
                            loading,
                          } = action;

                          if (typeof disabled === 'function') {
                            disabled = disabled(item, index);
                          }

                          if (disabled) {
                            color = 'default';
                          } else if (typeof color === 'function') {
                            color = color(item, index);
                          }

                          if (typeof hidden === 'function') {
                            hidden = hidden(item, index);
                          }

                          if (typeof icon === 'function') {
                            icon = icon(item, index);
                          }

                          if (typeof label === 'function') {
                            label = label(item, index);
                          }

                          if (typeof title === 'function') {
                            title = title(item, index);
                          }

                          if (typeof loading === 'function') {
                            loading = loading(item, index);
                          }

                          const Title = title ? Tooltip : Fragment;

                          return !hidden ? (
                            <>
                              <Title key={key} title={title}>
                                {action.label
                                  ? (<Button
                                    children={label}
                                    className={classes[`${color}Bg`]}
                                    disabled={disabled}
                                    onClick={() => handleAction(action, item, index)}
                                    size="small"
                                    style={{
                                      marginRight: '0.4em',
                                      marginLeft: '0.4em',
                                    }}
                                    startIcon={icon}
                                    variant="contained"
                                  />)
                                  : (<IconButton
                                    disabled={disabled || loading}
                                    onClick={() => handleAction(action, item, index)}
                                    size="small"
                                    variant="outlined"
                                  >
                                    {loading ? (
                                      <CircularProgress
                                        size={20}
                                        color={color}
                                        sx={{
                                          display: 'block',
                                        }}
                                      />
                                    ) : (
                                      <SvgIcon
                                        fontSize="small"
                                        color={color}
                                      >
                                        {icon}
                                      </SvgIcon>
                                    )}
                                  </IconButton>)}
                              </Title>
                            </>
                          ) : null;
                        })}
                      </TableCell>
                    )}
                  </StyledTableRow>
                );
              }))
            : (
              <StyledTableRow>
                <TableCell colSpan={(headers.length || 0) + (actions.some((action) => !action.hidden) ? 1 : 0)} align="center">
                  {noDataMessage ? noDataMessage : 'No se encontraron resultados.'}
                </TableCell>
              </StyledTableRow>
            )
        }
      </TableBody>

      {confirmState.open && (
        <Modal
          actions={[
            {
              color: 'secondary',
              fn: handleClose,
              label: confirmState.action.confirm.cancelText || 'Cancelar',
            },
            {
              color: 'primary',
              fn: handleConfirm,
              label: confirmState.action.confirm.okText || 'Confirmar',
            },
          ]}
          events={{
            onClose: handleClose,
          }}
          open={confirmState.open}
          title={confirmState.action.confirm.title || 'Confirmar acción'}
        >
          {typeof confirmState.action.confirm.message === 'function'
            ? confirmState.action.confirm.message(confirmState.item)
            : confirmState.action.confirm.message}
        </Modal>
      )}

      {footers && (<TableFooter>
        {footerSeparator && (<TableRow>
          <TableCell
            className={classes.footerSeparator}
            size="small" colSpan={6} />
        </TableRow>)}

        <TableRow>
          {footers.map((footer, index) => {
            let align = footer.align || 'center';
            let cellValue = footer;

            if (typeof footer === 'object' && !footer?.props) {
              cellValue = footer.label || null;

              if (footer.type === 'number') {
                align = 'right';
              }
            }

            return (<TableCell
              align={align}
              children={cellValue}
              key={index}
              size={size}
            />);
          })}
        </TableRow>
      </TableFooter>)}
    </Table>
  );

  return (
    <Box mt={mt} className={classes.root} hidden={hidden}>
      <Filters
        filters={filters}
        query={query}
        searchAction={searchAction}
        handleQueryChange={handleQueryChange}
      />

      {!!searchActionByDate && <Paper className={classes.filterCard}>
        <Grid container spacing={1}>
          <Grid item md={2} xs={6} />
          <Grid item md={2} xs={6} />
          <Grid item md={2} xs={6} />
          <Grid item md={4} xs={6}>
            <DateControl
              fullWidth
              label={t('search')}
              size="small"
              variant="outlined"
              value={query}
              onChange={handleQueryChangeByDate}
            />
          </Grid>
          <Grid item md={2} xs={6}>
            <Button
              children="Eliminar filtro"
              onClick={() => onDeleteFilterDate()}
              size="large"
              style={{
                margin: '0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              variant="contained"
            />
          </Grid>
        </Grid>
      </Paper>}

      <Card style={{
        borderRadius: '10px',
        position: 'relative',
      }}>
        {title && (<>
          <CardHeader
            title={title}
            subheader={subTitle}
            action={headerActionComponent}
          />
          <Divider />
        </>)}
        {isLoading && rows && rows.length > 0 && (
          <LoaderOverlay />
        )}

        {isLoading && rows && rows.length > 0 && <LoaderOverlay />}

        {isMobile
          ? (
            <Box>
              <Paper className={classes.root}>
                <TableContainer
                  ref={containerRef}
                  style={{
                    maxHeight: stickyHeader ? maxHeight : undefined,
                  }}
                  className={classes.scrollMobile}
                >
                  {renderTableContent()}
                </TableContainer>
              </Paper>
            </Box>)
          : (
            <PerfectScrollbar>
              <Box>
                <Paper className={classes.root}>
                  <TableContainer
                    ref={containerRef}
                    style={{
                      maxHeight: stickyHeader ? maxHeight : undefined,
                    }}
                    className={classes.customScrollBar}
                  >
                    {renderTableContent()}
                  </TableContainer>
                </Paper>
              </Box>
            </PerfectScrollbar>
          )}

        {!removePagination && (
          <TablePagination
            backIconButtonText={t('previous_page')}
            component="div"
            count={total}
            labelRowsPerPage={t('rows_per_page')}
            nextIconButtonText={t('next_page')}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={page - 1}
            rowsPerPage={limit}
            rowsPerPageOptions={perPageOptions}
          />
        )}
      </Card>
    </Box>
  );
}

DataTable.defaultProps = DEFAULT_PROPS;

DataTable.propTypes = PROP_TYPES;

export default DataTable;
